import { pozycjeSprzetu } from './handel'
import { wymagaPpoz } from '../../config/ppoz'
import { typKonstrukcji } from './wycenaKlienta'
import { kalendarzZDanych, zTerminowKarty } from './kalendarz'

/**
 * Prowadzony przepływ etapów klienta (decyzje właściciela 2026-10-11). Etap nie zmienia się sam: karta liczy
 * z danych, czy warunki etapu są spełnione, pokazuje je jako zadania i proponuje następny etap (kolejność firmy),
 * a zmianę zatwierdza jedno kliknięcie. Reguły są tylko dla podpowiedzi startowych (pole `krok` etapu); etap bez
 * znanego kroku przestawia się ręcznie jak wcześniej. Propozycje idą tylko do przodu: gdy dane przestaną spełniać
 * warunki poprzedniego etapu, karta ostrzega, a etap zostaje.
 *
 * Fakty biorą się z pól, które już są w karcie: terminy (`dane.kalendarz`), zestawy, cena, stan sprzętu per
 * składnik (`dane.sprzet.pozycje`), teczka PME2, przełączniki narzędzi. Nowe pola tylko tam, gdzie faktu nie było
 * gdzie zapisać (`ZNACZNIKI`): `dane.umowa` ({podpisana, data}) i `dane.zgloszenieOsd` ({wyslane, data}); formularz
 * zgłoszenia do operatora niczego w karcie nie zostawia, więc wysłanie zaznacza się ręcznie (decyzja 2026-10-11).
 *
 * „Formalności” (2026-10-11): jeden etap zamiast „Zgłoszenie OSD” i „Zgłoszenie dotacji”. Zgłoszenie do operatora,
 * zawiadomienie do PSP i wniosek PME2 robią różne osoby w różnych terminach, w dowolnej kolejności, więc to trzy
 * znaczniki jednego etapu (`dane.zawiadomieniePsp`, `dane.wniosekPme2` obok `dane.zgloszenieOsd`), każdy tylko
 * przy narzędziu widocznym w karcie. „Zakończony montaż” nie ma już warunków: od razu proponuje „Formalności”.
 * Stare kroki `zgloszenie_osd` i `dotacja` zostają dla firm z własnymi etapami sprzed tej zmiany.
 *
 * Zadania z `naLiscie` (zestaw, zamówiony, dojechał) liczą się w regułach jak każde inne, ale „Co dalej” ich
 * nie wypisuje: przy zamawianiu i dostawie widać je na liście sprzętu z przełącznikami (prośba 2026-10-11).
 *
 * Runda 4 (2026-10-11): etap montażu ma dwa zadania, „Montaż rozpoczęty” i „Montaż zakończony” (status terminu
 * `rozpoczety` między zaplanowanym a zakończonym, potwierdzany jednym kliknięciem w „Terminach”).
 */

/** Znaczniki zaznaczane w karcie: zadanie → pole w `dane` i klucz „zrobione”; data zaznaczenia obok. */
export const ZNACZNIKI = {
  umowa: { pole: 'umowa', klucz: 'podpisana' },
  osdWyslane: { pole: 'zgloszenieOsd', klucz: 'wyslane' },
  pspWyslane: { pole: 'zawiadomieniePsp', klucz: 'wyslane' },
  pme2Zlozony: { pole: 'wniosekPme2', klucz: 'zlozony' },
}
export const znacznik = (dane, zadanie) => {
  const z = ZNACZNIKI[zadanie]
  const v = z && dane?.[z.pole]
  return { zrobione: Boolean(v?.[z?.klucz]), data: v?.data || '' }
}
export const ustawZnacznik = (dane, zadanie, zrobione, dzis) => {
  if (zadanie === MONTAZ_POTWIERDZONY) return potwierdzMontaz(dane, zrobione, dzis)
  const z = ZNACZNIKI[zadanie]
  return { ...dane, [z.pole]: { [z.klucz]: zrobione, data: zrobione ? dzis : '' } }
}

/**
 * Runda 4 (2026-10-11), etap dostawy: montaż umówiony, ale niepotwierdzony z klientem, nie wstrzymuje etapu, tylko
 * „Co dalej” pyta pod zadaniem, czy termin jest potwierdzony. Przełącznik zapisuje `ustalony` w samym terminie
 * (`dane.kalendarz`), tym samym polu co „Termin pewny” w oknie terminu; idzie przez `ustawZnacznik` karty.
 */
export const MONTAZ_POTWIERDZONY = 'montazPotwierdzony'
const terminyZDanych = (dane) => (Array.isArray(dane?.kalendarz) ? kalendarzZDanych(dane.kalendarz) : (zTerminowKarty(dane) || []))

/** Zaplanowany montaż, o który pyta się przy dostawie: najbliższy od dziś, a gdy wszystkie minęły, ostatni. */
export const montazDoPotwierdzenia = (dane, dzis) => {
  const m = terminyZDanych(dane).filter((t) => t.rodzaj === 'montaz' && t.status === 'zaplanowany').sort((a, b) => a.od.localeCompare(b.od))
  return m.find((t) => t.do >= dzis) || m[m.length - 1] || null
}

export const potwierdzMontaz = (dane, potwierdzony, dzis) => {
  const m = montazDoPotwierdzenia(dane, dzis)
  if (!m) return dane
  return { ...dane, kalendarz: terminyZDanych(dane).map((t) => (t.id === m.id ? { ...t, ustalony: Boolean(potwierdzony) } : t)) }
}

/** Rodzaje sprzętu w podpiętych zestawach (BMS idzie z magazynem). */
export const rodzajeZestawow = (zestawy) => [...new Set((zestawy || [])
  .flatMap((z) => (z.payload?.items || []).map((it) => it.category || 'inne')))].filter((r) => r !== 'bms')

/**
 * Fakty o kliencie potrzebne regułom, z danych karty. Konstrukcja wybrana w karcie (`dane.konstrukcja.typ`) to
 * też pozycja do zamówienia (stan pod kluczem `konstrukcja` w `dane.sprzet.pozycje`). `mocKwp` decyduje, czy
 * zawiadomienie do PSP w ogóle obowiązuje (do 6,5 kWp nie); nieznana moc go nie skreśla.
 */
export const faktyKlienta = ({ dane = {}, terminy = [], zestawy = [], cena = {}, widoczne = {}, teczka = null, mocKwp = null }) => {
  const rodzaje = [...rodzajeZestawow(zestawy), ...(typKonstrukcji(dane.konstrukcja) ? ['konstrukcja'] : [])]
  const stany = Object.values(pozycjeSprzetu(dane.sprzet, rodzaje))
  const jest = (rodzaj, statusy) => terminy.some((t) => t.rodzaj === rodzaj && statusy.includes(t.status))
  return {
    spotkanieZakonczone: jest('spotkanie', ['zakonczony']),
    // Montaż w dwóch krokach (runda 4): zaplanowany → rozpoczęty → zakończony; późniejszy krok spełnia wcześniejsze.
    montazUmowiony: jest('montaz', ['zaplanowany', 'rozpoczety', 'zakonczony']),
    montazRozpoczety: jest('montaz', ['rozpoczety', 'zakonczony']),
    montazZakonczony: jest('montaz', ['zakonczony']),
    zestaw: zestawy.length > 0,
    cena: cena?.brutto != null || cena?.netto != null,
    // Oferta przygotowana w karcie (numer w `dane.oferta`, components/klienci/OfertaKlienta).
    oferta: Boolean(dane.oferta?.numer),
    umowa: znacznik(dane, 'umowa').zrobione,
    osdWyslane: znacznik(dane, 'osdWyslane').zrobione,
    pspWyslane: znacznik(dane, 'pspWyslane').zrobione,
    pme2Zlozony: znacznik(dane, 'pme2Zlozony').zrobione,
    sprzetZamowiony: stany.length > 0 && stany.every((s) => s.zamowiony),
    sprzetDojechal: stany.length > 0 && stany.every((s) => s.dojechal),
    teczka: Boolean(teczka),
    osd: widoczne.osd !== false,
    pme2: widoczne.pme2 !== false,
    psp: widoczne.psp !== false && (mocKwp == null || wymagaPpoz(mocKwp)),
  }
}

// Zadanie: id, tekst, czy spełnione, akcja w karcie (dokąd prowadzi), czy wstrzymuje propozycję i czy widać je
// na liście sprzętu zamiast na liście zadań.
const Z = (id, tekst, spelnione, akcja, { blokuje = true, naLiscie = false } = {}) => ({ id, tekst, spelnione, akcja, blokuje, naLiscie })
const NA_LISCIE = { naLiscie: true }

/** Zadania etapu o danym kroku; null, gdy krok nie ma reguł (etap własny firmy). */
export const zadaniaEtapu = (krok, f) => {
  switch (krok) {
    case 'spotkanie': return [
      Z('spotkanie', 'Spotkanie odbyte (zakończone w terminach)', f.spotkanieZakonczone, 'terminy'),
      Z('zestaw', 'Zestaw podpięty do klienta', f.zestaw, 'zestaw'),
      Z('cena', 'Cena dla klienta ustalona', f.cena, 'cena'),
    ]
    // Oferta to zadanie etapu, w którym klient decyduje o podpisie: najpierw przygotowana oferta, potem umowa
    // (decyzja właściciela 2026-10-11).
    case 'oferta': return [
      Z('oferta', 'Oferta przygotowana', f.oferta, 'oferta'),
      Z('umowa', 'Umowa podpisana', f.umowa, 'umowa'),
    ]
    case 'podpisany': return []
    case 'zamawianie': return [
      Z('zestaw', 'Zestaw podpięty do klienta', f.zestaw, 'zestaw', NA_LISCIE),
      Z('zamowiony', 'Każdy składnik zestawu zamówiony', f.sprzetZamowiony, 'zestaw', NA_LISCIE),
    ]
    case 'dostawa': return [
      Z('dojechal', 'Każdy składnik zestawu dojechał', f.sprzetDojechal, 'zestaw', NA_LISCIE),
      Z('montaz', 'Montaż umówiony w terminach', f.montazUmowiony, 'terminy'),
    ]
    case 'montaz': return [
      Z('montazStart', 'Montaż rozpoczęty (w terminach)', f.montazRozpoczety, 'terminy'),
      Z('montazKoniec', 'Montaż zakończony (w terminach)', f.montazZakonczony, 'terminy'),
    ]
    case 'po_montazu': return []
    case 'formalnosci': return [
      f.osd && Z('osdWyslane', 'Zgłoszenie do operatora sieci wysłane', f.osdWyslane, 'osdWyslane'),
      f.psp && Z('pspWyslane', 'Zawiadomienie do PSP wysłane', f.pspWyslane, 'pspWyslane'),
      f.pme2 && Z('pme2Zlozony', 'Wniosek PME2 złożony', f.pme2Zlozony, 'pme2Zlozony'),
    ].filter(Boolean)
    case 'zgloszenie_osd': return f.pme2 ? [Z('teczka', 'Teczka PME2 założona', f.teczka, 'teczka')] : []
    case 'dotacja': return []
    default: return null
  }
}

/**
 * Ocena karty: zadania bieżącego etapu, następny etap w kolejności firmy, czy można go zaproponować,
 * i ostrzeżenia, gdy poprzedni etap ma niespełnione warunki (np. odznaczony „dojechał” na etapie montażu).
 */
export const ocenaEtapu = ({ etap, etapy = [], fakty }) => {
  const i = etapy.findIndex((e) => e.id === etap?.id)
  const nastepny = i >= 0 ? etapy[i + 1] || null : null
  const zadania = zadaniaEtapu(etap?.krok, fakty)
  const poprzedni = i > 0 ? etapy[i - 1] : null
  const ostrzezenia = poprzedni ? (zadaniaEtapu(poprzedni.krok, fakty) || [])
    .filter((z) => z.blokuje && z.spelnione === false).map((z) => ({ ...z, etap: poprzedni })) : []
  if (!zadania) return { prowadzony: false, zadania: [], nastepny, gotowe: false, brakuje: [], ostrzezenia }
  const brakuje = zadania.filter((z) => z.blokuje && !z.spelnione)
  return { prowadzony: true, zadania, nastepny, gotowe: Boolean(nastepny) && brakuje.length === 0, brakuje, ostrzezenia }
}

/**
 * Ile pełnych dni klient jest w bieżącym etapie: z `dane.etapOd` ({id, data}), które ustawia serwer przy każdej
 * zmianie etapu (`accounts/api.py` `ETAP_OD`). Null, gdy data dotyczy innego etapu albo jej nie ma (klient sprzed
 * 2026-10-11): wtedy lepiej nic nie pokazać niż zgadywać.
 */
export const dniWEtapie = (etapOd, status, dzis) => {
  if (!etapOd || etapOd.id !== status || !/^\d{4}-\d{2}-\d{2}$/.test(etapOd.data || '')) return null
  const dni = Math.round((new Date(`${dzis}T12:00:00`) - new Date(`${etapOd.data}T12:00:00`)) / 864e5)
  return Number.isFinite(dni) ? Math.max(0, dni) : null
}

/**
 * Pasek postępu nad kafelkami karty (2026-10-11): jedna oś, etapy firmy od lewej do prawej. Każdy etap to odcinek:
 * przebyty, bieżący (wypełniony w proporcji zrobionych zadań etapu) albo przed klientem. Klient bez etapu ma
 * wszystkie odcinki przed sobą. `zadania` jest null przy etapie bez reguł (własnym firmy) i przy etapie bez zadań.
 */
export const postepProcesu = ({ etap, etapy = [], ocena, dni = null }) => {
  const i = etapy.findIndex((e) => e.id === etap?.id)
  const wszystkie = ocena?.prowadzony ? ocena.zadania.filter((z) => z.blokuje) : []
  return {
    numer: i >= 0 ? i + 1 : null,
    liczba: etapy.length,
    odcinki: etapy.map((e, j) => ({ ...e, stan: i < 0 || j > i ? 'przed' : j < i ? 'przebyty' : 'biezacy' })),
    zadania: wszystkie.length ? { zrobione: wszystkie.filter((z) => z.spelnione).length, wszystkie: wszystkie.length } : null,
    nastepny: ocena?.nastepny || (i >= 0 ? etapy[i + 1] || null : null),
    dni,
  }
}

/** „od dziś”, „1 dzień”, „12 dni”; null bez liczby. */
export const opisDni = (n) => {
  if (n == null) return null
  if (n === 0) return 'od dziś'
  return n === 1 ? '1 dzień' : `${n} dni`
}
